import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useRequest } from 'umi';
import styles from '../css/campCSS.less';
import { MarkingDetailsVO, MarkingVO, MarkingForm } from '@/types/camp/admin/markingType';
import { Form, Button, message, InputNumber, Popconfirm, Skeleton, Empty } from 'antd';
import Editor from '@digit-fe/digit-editor-new';
import { debounce } from 'lodash';
import {
  listSubjectiveQuestion,
  saveSubjectiveQuestion,
} from '@/utils/requests/camp/requests_marking';
import { DownOutlined, UpOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { editParseJson } from '@/utils/index';
import { onFileAndVideoUpload, onImageUpload } from '@/pages/camp/components/editor/uploadUtil';
import QuestionMarkingComment from './questionMarkingComment';

interface Props {
  baseInfo?: MarkingVO;
  studyNodeId: string;
  setHash?: (hash: number) => void;
}

const QuestionMarking: React.FC<Props> = ({ studyNodeId, baseInfo, setHash }) => {
  const [dataSource, setDataSource] = useState<MarkingDetailsVO[]>([]);
  const [itemDataSource, setItemDataSource] = useState<MarkingDetailsVO[]>([]);
  const editDataRef = useRef<any>('');
  const [form] = Form.useForm();
  const [expandedIndexes, setExpandedIndexes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [formInit, setFormInit] = useState(false);
  const [saveText, setSaveText] = useState('');
  const [cacheName, setCacheName] = useState('');
  const editUploadRef = useRef(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 获取主观题列表
  const { run: listSubjectiveRequest } = useRequest(
    () => {
      return listSubjectiveQuestion(studyNodeId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setDataSource(res);
        setItemDataSource(res);
        setLoading(false);
        setFormInit(true);
        localStorage.setItem(`marking_${studyNodeId}`, JSON.stringify(res));
        setCacheName(`marking_${studyNodeId}`);
      },
    },
  );
  useEffect(() => {
    listSubjectiveRequest();
  }, []);

  // 保存主观题
  const { run: saveQuestionRequest } = useRequest(
    (data) => {
      return saveSubjectiveQuestion(data);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setSaveText('自动保存成功');
      },
    },
  );
  // 提交主观题
  const { run: submitQuestionRequest } = useRequest(
    (data) => {
      return saveSubjectiveQuestion(data);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setConfirmLoading(false);
        setHash && setHash(Math.random());
        message.success('提交成功');
      },
    },
  );

  // 自动保存
  useEffect(() => {
    const submitTimer = () => {
      setSaveText('正在保存');
      let storedArray = editParseJson(localStorage.getItem(cacheName) || '');
      const formData = {
        submit: false,
        nodeId: baseInfo?.nodeId,
        studyNodeId: studyNodeId,
        list: storedArray,
      };
      storedArray && saveQuestionRequest(formData);
    };
    const intervalId = setInterval(submitTimer, 60000);
    return () => clearInterval(intervalId);
  }, [cacheName]);

  // 在组件加载时设置表单的初始值
  useEffect(() => {
    // 获取初始数据
    if (formInit) {
      let initialData: any = {};
      dataSource.forEach((item) => {
        initialData[`score_${item.id}`] = item.score;
      });
      form.setFieldsValue(initialData);
    }
  }, [formInit]);

  // 更新toggleItem函数以支持多选
  const toggleItem = (index: string) => {
    let newExpandedIndexes = [...expandedIndexes];
    const indexPosition = expandedIndexes.indexOf(index);

    if (indexPosition > -1) {
      // 如果已存在，则移除它
      newExpandedIndexes = newExpandedIndexes.filter((i) => i !== index);
    } else {
      // 否则添加新的索引
      newExpandedIndexes.push(index);
    }

    // 更新状态
    setExpandedIndexes(newExpandedIndexes);
  };

  const zhedie = (index: any) => {
    if (expandedIndexes.includes(index)) {
      return (
        <div className="col008 flex_1">
          <span className="f14" style={{ marginRight: 6 }}>
            折叠
          </span>
          <UpOutlined className="f12" />
        </div>
      );
    } else {
      return (
        <div className="col008 flex_1">
          <span className="f14" style={{ marginRight: 6 }}>
            展开
          </span>
          <DownOutlined className="f12" />
        </div>
      );
    }
  };

  // 必填滚动
  const handleFinishFailed = (errorInfo: any) => {
    if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
      const firstErrorField = errorInfo.errorFields[0];
      console.log('必填项目', firstErrorField);
      const firstErrorElement = document.getElementById(firstErrorField.name + '_score');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  let debouncedUpdate = debounce((id: string, data: any) => {
    // console.log('editUploadRef.current', editUploadRef.current);
    if (!editUploadRef.current) {
      let newData = JSON.stringify(data);
      updateComment(id, newData);
    }
  }, 800);

  const updateComment = useCallback((id: string, newValue: any) => {
    console.log('newValue', newValue);
    setDataSource((prev) =>
      prev.map((q) =>
        q.id === id
          ? {
              ...q,
              comment: newValue,
            }
          : q,
      ),
    );
  }, []);

  // const updateComment = useCallback(
  //   (id: string, newValue: any) => {
  //     console.log('newValue', newValue);
  //     const item = dataSource.find((q) => q.id === id);
  //     if (item) {
  //       item.comment = newValue;
  //     }
  //   },
  //   [dataSource],
  // );

  let debouncedScore = debounce((id: string, newValue: any) => {
    updateScore(id, newValue);
  }, 500);

  const updateScore = useCallback((id: string, newValue: any) => {
    setDataSource((prev) =>
      prev.map((q) =>
        q.id === id
          ? {
              ...q,
              score: newValue,
            }
          : q,
      ),
    );
  }, []);

  // 实时保存至浏览器缓存
  useEffect(() => {
    if (dataSource) {
      localStorage.setItem(cacheName, JSON.stringify(dataSource));
    }
  }, [dataSource]);

  // 构造表单提交
  const handleSubmit = async () => {
    setConfirmLoading(true);
    try {
      // 验证习题
      await form.validateFields();
      // 提交
      const formData = {
        submit: true,
        nodeId: baseInfo?.nodeId,
        studyNodeId: studyNodeId,
        list: dataSource,
      };
      submitQuestionRequest(formData);
    } catch (error: any) {
      if (error.errorFields && error.errorFields.length > 0) {
        message.warning('请填学员得分');
      }

      handleFinishFailed(error);
    }
  };

  const handleItem = (item: MarkingDetailsVO, index: number) => {
    return (
      <div className={styles.marking_form_item}>
        <div className="pd20 edit_no_pd" style={{ position: 'relative' }}>
          <div
            className="flex_3"
            onClick={() => toggleItem(`timu${index}`)}
            style={{ cursor: 'pointer', paddingBottom: 6 }}
          >
            第 {index + 1}/{dataSource?.length} 题：
            {zhedie(`timu${index}`)}
          </div>
          <div className={expandedIndexes.includes(`timu${index}`) ? styles.show : styles.hide}>
            <div className="f16 fw600" style={{ paddingBottom: 6 }}>
              {item.title}
            </div>
            <Editor
              key={`edit_description_${index}`}
              content={item.description && JSON.parse(item.description)}
              editable={false}
              titleProps={{ showTitle: false }}
              catalogProps={{ showCatalog: false }}
            />
          </div>
        </div>

        {item.analysis && (
          <div className="edit_no_pd">
            <div className="com_line"></div>
            <div style={{ padding: '0px 20px' }}>
              <div
                className="flex_3"
                onClick={() => toggleItem(`jiexi${index}`)}
                style={{ cursor: 'pointer' }}
              >
                题目解析：
                {zhedie(`jiexi${index}`)}
              </div>
            </div>
            <div
              style={{ padding: '16px 20px 0 20px' }}
              className={expandedIndexes.includes(`jiexi${index}`) ? styles.show : styles.hide1}
            >
              <Editor
                key={`analysis_${index}`}
                content={item.analysis && JSON.parse(item.analysis)}
                editable={false}
                titleProps={{ showTitle: false }}
                catalogProps={{ showCatalog: false }}
              />
            </div>
          </div>
        )}
        <div className="com_line"></div>
        <div style={{ paddingLeft: 16 }}>学员答案：</div>
        <div className="pd20 edit_no_pd">
          <Editor
            key={`analysis_${index}`}
            content={item.studentAnswer && JSON.parse(item.studentAnswer)}
            editable={false}
            titleProps={{ showTitle: false }}
            catalogProps={{ showCatalog: false }}
          />
        </div>

        <div className="com_line"></div>
        <div style={{ paddingLeft: 20 }}>
          <div className="fw600 com_required" id={`score_${item.id}_score`}>
            本题得分（满分{item.rewardScore}）
          </div>
          <Form.Item
            key={index}
            name={`score_${item.id}`}
            rules={[{ required: true, message: '请输入分数' }]}
          >
            <InputNumber
              min={0}
              max={item.rewardScore}
              value={item.score}
              addonAfter="分"
              onChange={(value) => {
                debouncedScore(item.id, value);
              }}
            />
          </Form.Item>
        </div>
        <div className="pd20">
          <div className="fw600" style={{ paddingBottom: 6 }}>
            评语
          </div>
          <Editor
            key={`edit_${item.id}`}
            content={editParseJson(item.comment)}
            style={{ height: 360, border: '1px solid #E5E5E5' }}
            titleProps={{ showTitle: false }}
            catalogProps={{ showCatalog: false }}
            onChange={(data) => {
              debouncedUpdate(item.id, data);
            }}
            videoProps={{
              onVideoStartUpload: () => {
                editUploadRef.current = true;
              },
              onVideoEndUpload: () => {
                setTimeout(function () {
                  editUploadRef.current = false;
                }, 500);
              },
              onVideoUpload: onFileAndVideoUpload,
            }}
            attachProps={{
              onAttachStartUpload: () => {
                console.log('触发了上附件');
                editUploadRef.current = true;
              },
              onAttachEndUpload: () => {
                setTimeout(function () {
                  console.log('上附件结束');
                  editUploadRef.current = false;
                }, 500);
              },
              onAttachUpload: onFileAndVideoUpload,
            }}
            imageProps={{
              onImageStartUpload: () => {
                editUploadRef.current = true;
              },
              onImageEndUpload: () => {
                setTimeout(function () {
                  editUploadRef.current = false;
                }, 500);
              },
              onImageUpload: onImageUpload,
            }}
          />
        </div>
      </div>
    );
  };

  const renderedItems = useMemo(() => {
    return itemDataSource.map((item, index) => <div key={index}>{handleItem(item, index)}</div>);
  }, [itemDataSource, expandedIndexes]);

  return (
    <>
      {loading && (
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 24, padding: '10px 0' }}>
            <Skeleton.Button active block style={{ height: 40 }} />
          </div>

          <div className="flex_0">
            <div style={{ width: 120 }}>
              <Skeleton.Image active />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton active loading={loading} paragraph={{ rows: 10 }} />
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ borderRadius: 8 }}>
          <div style={{ padding: '12px 20px 12px 20px', borderBottom: '1px solid #E5E5E5' }}>
            <div className="f16">{`[${baseInfo?.batchName || baseInfo?.className}] ${
              baseInfo?.campName || baseInfo?.projectName
            } - ${baseInfo?.nodeName}`}</div>
            <div className="f12 col808">{baseInfo?.username} 的作业</div>
          </div>

          <Form
            className={styles.marking_form}
            form={form}
            preserve={false}
            autoComplete="off"
            layout="vertical"
          >
            {dataSource && dataSource?.length > 0 ? (
              renderedItems
            ) : (
              <Empty style={{ margin: '40px 0 20px' }} />
            )}
          </Form>
          <div className={styles.submit_box}>
            <div className="flex_1">
              <Popconfirm
                title={<div style={{ padding: '0 80px 20px 0' }}>确认提交成绩吗？</div>}
                okButtonProps={{ loading: confirmLoading }}
                onConfirm={() => {
                  handleSubmit();
                }}
                okText="提交"
                cancelText="取消"
              >
                <Button
                  style={{ width: 140, height: 34, fontSize: 14, marginRight: 18 }}
                  type="primary"
                  htmlType="submit"
                  loading={confirmLoading}
                >
                  提交成绩
                </Button>
              </Popconfirm>

              <div className="f14">
                {saveText && (
                  <div>
                    {saveText === '自动保存成功' ? (
                      <div className="flex_1 col52c" style={{ gap: 4 }}>
                        <CheckCircleOutlined style={{ fontSize: 15 }} />
                        <span>{saveText}</span>
                      </div>
                    ) : (
                      <div className="flex_1 col808" style={{ gap: 4 }}>
                        <SyncOutlined style={{ fontSize: 15 }} />
                        <span>{saveText}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default QuestionMarking;
